import posthog from 'posthog-js';
import { EUserType } from 'types/accessControll.types';
import type { TAdminUser, TRecruiterAppUser } from 'types/user.types';

const FLAGS_TIMEOUT_MS = 3000;

/**
 * Identifies the signed-in user to PostHog and resolves once flags have been re-evaluated for them.
 *
 * The distinct id is the user's own id, and the person properties are the ones release conditions
 * target: `email` for everyone, `clientId` for recruiters. Resolving only after the reload matters:
 * flags first load for the anonymous visitor, so anything that reads a flag before this resolves
 * (the chat deciding which flow a new session gets) would see the wrong answer.
 *
 * Never rejects and never waits longer than FLAGS_TIMEOUT_MS; a blocked or slow PostHog must not
 * keep anyone out of the app.
 */
export function identifyPosthogUser(user: TAdminUser | TRecruiterAppUser): Promise<void> {
  const alreadyIdentified = posthog.get_distinct_id() === user._id;
  const clientId = user.userType === EUserType.RECRUITER ? (user as TRecruiterAppUser).client_id : undefined;

  return new Promise((resolve) => {
    if (alreadyIdentified) {
      posthog.identify(user._id, { email: user.email, ...(clientId ? { clientId } : {}) });
      resolve();
      return;
    }

    let stopListening = () => {};
    const finish = () => {
      clearTimeout(timer);
      stopListening();
      resolve();
    };
    const timer = setTimeout(finish, FLAGS_TIMEOUT_MS);

    // onFeatureFlags calls back straight away when flags are already loaded; that is the anonymous
    // visitor's set, so only a callback after registration counts.
    let registering = true;
    stopListening = posthog.onFeatureFlags(() => {
      if (!registering) finish();
    });
    registering = false;

    posthog.identify(user._id, { email: user.email, ...(clientId ? { clientId } : {}) });
  });
}
